import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    DatePicker,
    Input,
    Select,
    Checkbox,
    Button,
    Table,
    notification,
    Tooltip,
    Space,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
    Files,
    Folder,
    Trash,
    UploadSimple,
    FloppyDisk,
    ArrowSquareOut,
    CheckCircle,
    XCircle,
} from '@phosphor-icons/react';
import DashboardModal from '../DashboardModal';
import { useOneButtonStore } from '../../../../../store/oneButtonStore';
import { OBD_DASHBOARD_MODAL_Z } from '../oneButtonOverlayZ';
import documentsService, {
    CategoryOption,
    ContractStatusDetails,
    DocumentItem,
    ProjectBlockItem,
    RetrieveDocumentsResponse,
} from '../../../../../services/oneButtonServices/documentsService';
import {
    getOneButtonCapabilityValue,
    oneButtonCapabilityRawAllows,
} from '../../../../../services/oneButtonServices/capabilityService';
import './DocumentsModal.css';

const { TextArea } = Input;

export const DocumentsModal: React.FC = () => {
    const theme = useOneButtonStore((s: any) => s.theme);
    const open = useOneButtonStore((s) => s.documentsModalOpen);
    const setOpen = useOneButtonStore((s) => s.setDocumentsModalOpen);

    const opportunityID = useOneButtonStore((s) => s.opportunityID);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const storeSalesForceId = useOneButtonStore((s) => s.salesForceId);
    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);

    const enableSaveContractStatus = oneButtonCapabilityRawAllows(
        getOneButtonCapabilityValue(onebuttonCapabilities, 'Enable_Save_Contract_Status'),
    );
    const showUploadToSunnovaCap = oneButtonCapabilityRawAllows(
        getOneButtonCapabilityValue(onebuttonCapabilities, 'Show_Upload_to_Sunnova'),
    );

    const opportunityId =
        opportunityID ??
        opportunityDetails?.OpportunityId ??
        null;

    const [loading, setLoading] = useState<boolean>(false);
    const [savingStatus, setSavingStatus] = useState<boolean>(false);
    const [uploading, setUploading] = useState<boolean>(false);
    const [loadingBox, setLoadingBox] = useState<boolean>(false);

    const [documentData, setDocumentData] = useState<RetrieveDocumentsResponse | null>(null);
    const [categories, setCategories] = useState<CategoryOption[]>([]);
    const [contractDetails, setContractDetails] = useState<ContractStatusDetails>({});
    const [selectDate, setSelectDate] = useState<dayjs.Dayjs | null>(dayjs());

    // Interactive button state
    const [achVerification, setAchVerification] = useState<boolean>(false);
    const [contractValidation, setContractValidation] = useState<boolean>(false);

    // Upload Form State
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string | number | undefined>(undefined);
    const [uploadSunnova, setUploadSunnova] = useState<boolean>(false);
    const [selectedProjectBlock, setSelectedProjectBlock] = useState<string | number | undefined>(undefined);
    const [comments, setComments] = useState<string>('');
    const [formSubmitted, setFormSubmitted] = useState<boolean>(false);

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Box integration state
    const [showBox, setShowBox] = useState<boolean>(false);
    const [iframeFolderPath, setIframeFolderPath] = useState<string>('');

    const [noticeAPI, noticeContext] = notification.useNotification({ maxCount: 3 });

    // Load initial data
    const fetchDocumentsData = useCallback(async () => {
        if (!opportunityId) return;
        setLoading(true);
        try {
            const [docsRes, masterRes] = await Promise.all([
                documentsService.getDocuments(opportunityId),
                documentsService.getMasterDataByStep(5).catch(() => ({ Category: [] })),
            ]);

            setDocumentData(docsRes);
            setCategories(Array.isArray(masterRes?.Category) ? masterRes.Category : []);

            const status = docsRes?.ContractStatus;
            if (status) {
                setContractDetails(status);
                setSelectDate(status.InstallationDate ? dayjs(status.InstallationDate) : dayjs());
                setAchVerification(Boolean(status.ACHVerification));
                setContractValidation(Boolean(status.ContractValidation));
            } else {
                setContractDetails({});
                setSelectDate(dayjs());
                setAchVerification(false);
                setContractValidation(false);
            }

            if (docsRes?.ProjectBlocks && docsRes.ProjectBlocks.length > 0) {
                setSelectedProjectBlock(docsRes.ProjectBlocks[0].BlockId);
            }
        } catch (error) {
            console.error('Error fetching documents data:', error);
            noticeAPI.error({
                message: 'Failed to load documents',
                description: 'Unable to retrieve document details from the server.',
                placement: 'bottomRight',
            });
        } finally {
            setLoading(false);
        }
    }, [opportunityId, noticeAPI]);

    useEffect(() => {
        if (open && opportunityId) {
            void fetchDocumentsData();
        } else {
            // Reset upload form on modal close
            setSelectedFile(null);
            setFileName('');
            setSelectedCategory(undefined);
            setUploadSunnova(false);
            setComments('');
            setFormSubmitted(false);
            setShowBox(false);
            setIframeFolderPath('');
        }
    }, [open, opportunityId, fetchDocumentsData]);

    // Hide date picker or dropdowns when scrolling the modal while open
    const handleModalScroll = useCallback((e: Event) => {
        const target = e.target as Element | null;
        if (!target) return;

        // Do not close dropdowns when scrolling inside the dropdown option list itself
        if (
            target.closest?.('.ant-select-dropdown, .ant-picker-dropdown, .rc-virtual-list') ||
            (target.classList && (target.classList.contains('ant-select-dropdown') || target.classList.contains('ant-picker-dropdown')))
        ) {
            return;
        }

        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }, []);

    useEffect(() => {
        if (!open) return;

        // Capture scroll events from modal body or any child container
        window.addEventListener('scroll', handleModalScroll, { capture: true, passive: true });

        return () => {
            window.removeEventListener('scroll', handleModalScroll, { capture: true });
        };
    }, [open, handleModalScroll]);

    const isContractSigned = useMemo(() => {
        return documentData?.ContractStatus?.IsContractSigned === true;
    }, [documentData]);

    const projectBlocks: ProjectBlockItem[] = useMemo(() => {
        return Array.isArray(documentData?.ProjectBlocks) ? documentData.ProjectBlocks : [];
    }, [documentData]);

    const isUploadFormValid = useMemo(() => {
        if (!selectedFile || !selectedCategory) return false;
        if (showUploadToSunnovaCap && uploadSunnova && projectBlocks.length > 0 && !selectedProjectBlock) {
            return false;
        }
        return true;
    }, [selectedFile, selectedCategory, showUploadToSunnovaCap, uploadSunnova, projectBlocks.length, selectedProjectBlock]);

    const documentList: DocumentItem[] = useMemo(() => {
        const list = Array.isArray(documentData?.DocumentList) ? documentData.DocumentList : [];
        const formatted = list.map((item, idx) => ({
            ...item,
            key: item.DocumentId ?? idx,
            LastUpdatedOnFormatted: item.LastUpdatedOn
                ? dayjs(item.LastUpdatedOn).format('MM/DD/YYYY, h:mm:ss a')
                : '',
        }));

        return [...formatted].sort((a, b) => {
            const timeA = a.LastUpdatedOn ? dayjs(a.LastUpdatedOn).valueOf() : 0;
            const timeB = b.LastUpdatedOn ? dayjs(b.LastUpdatedOn).valueOf() : 0;
            return timeB - timeA;
        });
    }, [documentData]);

    // Handle File Selection
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size / (1024 * 1024) > 64) {
            noticeAPI.warning({
                message: 'File Size Exceeded',
                description: 'Uploaded file size should be less than 64MB.',
                placement: 'bottomRight',
            });
            if (fileInputRef.current) fileInputRef.current.value = '';
            setSelectedFile(null);
            setFileName('');
            return;
        }

        setSelectedFile(file);
        setFileName(file.name);
    };

    const handleDeleteFile = () => {
        setSelectedFile(null);
        setFileName('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Save Contract Status
    const handleSaveContractStatus = async () => {
        if (!opportunityId) return;

        if (!contractDetails.ContractId && !documentData?.ContractStatus?.ContractId) {
            noticeAPI.error({
                message: 'Contract ID is mandatory',
                placement: 'bottomRight',
            });
            return;
        }

        setSavingStatus(true);
        try {
            const payload = {
                Opportunity_Id: opportunityId,
                SalesForce_Id: documentData?.SalesForce_Id || (storeSalesForceId ?? ''),
                box__folder_id__c: documentData?.box__folder_id__c || '',
                ProjectBlocks: projectBlocks,
                ContractStatus: {
                    ...contractDetails,
                    InstallationDate: selectDate ? selectDate.format('YYYY-MM-DD') : null,
                    ACHVerification: achVerification,
                    ContractValidation: contractValidation,
                },
            };

            const res = await documentsService.saveContractStatus(payload);
            noticeAPI.success({
                message: res?.Message || 'Contract status saved successfully',
                placement: 'bottomRight',
            });
            void fetchDocumentsData();
        } catch (error) {
            console.error('Failed to save contract status:', error);
            noticeAPI.error({
                message: 'Contract Status Failed to save',
                placement: 'bottomRight',
            });
        } finally {
            setSavingStatus(false);
        }
    };

    // Handle Document Upload
    const handleUploadDocument = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormSubmitted(true);

        if (!selectedFile || !selectedCategory) {
            noticeAPI.error({
                message: 'Please fill all mandatory fields',
                placement: 'bottomRight',
            });
            return;
        }

        const categoryObj = categories.find((c) => String(c.Value) === String(selectedCategory));
        const projectBlockObj = projectBlocks.find((b) => String(b.BlockId) === String(selectedProjectBlock));

        const formData = new FormData();
        formData.append('uploadFile', selectedFile, fileName);
        formData.append('SalesForce_Id', documentData?.SalesForce_Id || (storeSalesForceId ?? ''));
        formData.append('DocumentId', '0');
        formData.append('Opportunity_Id', String(opportunityId));
        formData.append('FileName', fileName);
        formData.append('Category', categoryObj ? categoryObj.Text : '');
        formData.append('CatogoryId', String(selectedCategory));
        formData.append('Comments', comments);
        formData.append('ProjectBlockId', uploadSunnova && projectBlockObj ? String(projectBlockObj.BlockId) : '');
        formData.append('ProjectBlockName', uploadSunnova && projectBlockObj ? projectBlockObj.BlockName : '');
        formData.append('UploadToSunnova', String(uploadSunnova));

        setUploading(true);
        try {
            const res = await documentsService.uploadDocuments(formData);
            if (res?.data === 'sucess' || res?.Message?.toLowerCase().includes('success') || !res?.error) {
                noticeAPI.success({
                    message: 'Successfully Uploaded',
                    placement: 'bottomRight',
                });
                handleDeleteFile();
                setSelectedCategory(undefined);
                setComments('');
                setFormSubmitted(false);
                void fetchDocumentsData();
            } else {
                noticeAPI.error({
                    message: 'Uploading Failed',
                    description: res?.Message || 'An unexpected error occurred.',
                    placement: 'bottomRight',
                });
            }
        } catch (error: any) {
            console.error('Upload document failed:', error);
            noticeAPI.error({
                message: 'Uploading Failed',
                description: error?.response?.data?.Message,
                placement: 'bottomRight',
            });
        } finally {
            setUploading(false);
        }
    };

    // Handle Load Box Files
    const handleLoadBoxFiles = async () => {
        if (showBox) {
            setShowBox(false);
            return;
        }

        setLoadingBox(true);
        try {
            const sfId = documentData?.SalesForce_Id || storeSalesForceId;
            let folderId = documentData?.box__folder_id__c;

            if (!folderId && sfId) {
                const folderRes = await documentsService.getBoxFolderId(sfId);
                folderId = folderRes?.folderID;
            }

            const targetBoxId = folderId || documentData?.box__folder_id__c;
            if (targetBoxId) {
                void documentsService.getBoxFilesDetails(targetBoxId).catch((err) => {
                    console.error('Error fetching Box files details:', err);
                });
            }

            if (folderId) {
                const embedRes = await documentsService.getEmbedFolderUrl(folderId);
                if (embedRes?.embed_link) {
                    setIframeFolderPath(`${embedRes.embed_link}&partner_id=591`);
                } else {
                    setIframeFolderPath('');
                }
            }
            setShowBox(true);
        } catch (error) {
            console.error('Failed to load Box files:', error);
            noticeAPI.error({
                message: 'Unable to get Box files preview',
                placement: 'bottomRight',
            });
            setShowBox(true);
        } finally {
            setLoadingBox(false);
        }
    };

    // Format column text value safely to prevent rendering 'null' or 'undefined' as string
    const formatColumnText = (val: any) => {
        if (val === null || val === undefined) return '-';
        const str = String(val).trim();
        if (str === '' || str.toLowerCase() === 'null' || str.toLowerCase() === 'undefined') {
            return '-';
        }
        return str;
    };

    // Table Columns
    const columns: ColumnsType<DocumentItem & { key: any; LastUpdatedOnFormatted?: string }> = [
        {
            title: 'File Name',
            dataIndex: 'FileName',
            key: 'FileName',
            render: (text: string, record: DocumentItem) => {
                const path = record.FilePath || text;
                const formattedName = formatColumnText(text || record.FilePath);
                const displayName = formattedName !== '-' ? (text || record.FilePath) : 'Document';
                return (
                    <a
                        className="obd-docs-file-link"
                        href={path}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={displayName}
                        onClick={(e) => {
                            if (!path || path === '#') {
                                e.preventDefault();
                            }
                        }}
                    >
                        <span className="obd-docs-file-text">{displayName}</span><span className="obd-docs-file-icon-wrap">
                            {'\u00A0'}
                            <ArrowSquareOut size={14} className="obd-docs-file-icon" />
                        </span>
                    </a>
                );
            },
        },
        {
            title: 'Category',
            dataIndex: 'Category',
            key: 'Category',
            width: 200,
            render: (cat: string) => formatColumnText(cat),
        },
        {
            title: 'Comments',
            dataIndex: 'Comments',
            key: 'Comments',
            width: 240,
            render: (comm: string) => formatColumnText(comm),
        },
        {
            title: 'Updated On',
            dataIndex: 'LastUpdatedOnFormatted',
            key: 'LastUpdatedOnFormatted',
            width: 200,
            render: (date: string) => formatColumnText(date),
        },
        {
            title: 'Finance Partner Project Block Name',
            dataIndex: 'ProjectBlockName',
            key: 'ProjectBlockName',
            width: 220,
            render: (name: string) => formatColumnText(name),
        },
    ];

    const isPropertyOwnerSigned = Boolean(contractDetails.PropertyOwnerSignature);
    const isCreditPasserSigned = Boolean(contractDetails.CreditPasserSignature);

    return (
        <>
            {noticeContext}
            <DashboardModal
                open={open}
                onClose={() => setOpen(false)}
                title={
                    <>
                        <Files size={20} weight="duotone" aria-hidden />
                        <span>Documents</span>
                    </>
                }
                width="min(96vw, 1400px)"
                centered
                zIndex={OBD_DASHBOARD_MODAL_Z}
                theme={theme}
                loading={loading}
                className="obd-documents-modal"
                rootClassName="obd-documents-modal-root"
                footer={
                    <Button type="default" onClick={() => setOpen(false)}>
                        Close
                    </Button>
                }
            >
                <div className={`obd-documents-modal__shell obd-theme-${theme}`}>
                    {/* Top Row: Save Contract Status & Upload New Documents */}
                    <div className={`obd-docs-top-row ${!enableSaveContractStatus ? 'obd-docs-top-row--single' : ''}`}>
                        {/* Panel 1: Save Contract Status */}
                        {enableSaveContractStatus && (
                            <div className="obd-docs-panel">
                                <div className="obd-docs-panel__header">
                                    <span>Save Contract Status</span>
                                    <Button
                                        type="primary"
                                        size="small"
                                        icon={<FloppyDisk size={14} weight="bold" />}
                                        loading={savingStatus}
                                        onClick={handleSaveContractStatus}
                                    >
                                        Save
                                    </Button>
                                </div>
                                <div className="obd-docs-panel__body">
                                    <div className="obd-docs-contract-grid">
                                        {/* Left: Static Fields */}
                                        <div className="obd-docs-static-group">
                                            <div className="obd-docs-static-item">
                                                <div className="obd-docs-static-label">Finance Partner</div>
                                                <div className="obd-docs-static-val">
                                                    {contractDetails.FinancePartner || '-'}
                                                </div>
                                            </div>
                                            <div className="obd-docs-static-item">
                                                <div className="obd-docs-static-label">Contract Type</div>
                                                <div className="obd-docs-static-val">
                                                    {contractDetails.ContractType || '-'}
                                                </div>
                                            </div>
                                            <div className="obd-docs-static-item">
                                                <div className="obd-docs-static-label">Contract ID</div>
                                                <div className="obd-docs-static-val">
                                                    {contractDetails.ContractId || '-'}
                                                </div>
                                            </div>
                                            <div className="obd-docs-static-item">
                                                <div className="obd-docs-static-label">Contract Expiration</div>
                                                <div className="obd-docs-static-val">
                                                    {contractDetails.ContractExpiration || '-'}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right: Installation Date & Action Buttons */}
                                        <div className="obd-docs-interactive-group">
                                            <div className="obd-docs-field">
                                                <label className="obd-docs-field-label">Installation Date</label>
                                                <DatePicker
                                                    value={selectDate}
                                                    onChange={(d) => setSelectDate(d)}
                                                    format="MM-DD-YYYY"
                                                    style={{ width: '100%' }}
                                                />
                                            </div>

                                            <div className="obd-docs-status-buttons-grid">
                                                <button
                                                    type="button"
                                                    className={`obd-docs-status-btn ${isPropertyOwnerSigned ? 'is-active' : ''}`}
                                                    disabled
                                                >
                                                    Property Owner Signature
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`obd-docs-status-btn ${isCreditPasserSigned ? 'is-active' : ''}`}
                                                    disabled
                                                >
                                                    Credit Passer Signature
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`obd-docs-status-btn ${achVerification ? 'is-active' : ''}`}
                                                    onClick={() => setAchVerification(!achVerification)}
                                                >
                                                    ACH Verification
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`obd-docs-status-btn ${contractValidation ? 'is-active' : ''}`}
                                                    onClick={() => setContractValidation(!contractValidation)}
                                                >
                                                    Contract Validation
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Panel 2: Upload New Documents */}
                        <div className="obd-docs-panel">
                            <form onSubmit={handleUploadDocument} className="obd-docs-upload-form">
                                <div className="obd-docs-panel__header">
                                    <span>Upload New Documents</span>
                                    {isUploadFormValid && (
                                        <Button
                                            type="primary"
                                            size="small"
                                            htmlType="submit"
                                            icon={<UploadSimple size={14} weight="bold" />}
                                            loading={uploading}
                                        >
                                            Upload
                                        </Button>
                                    )}
                                </div>
                                <div className="obd-docs-panel__body">
                                    <div className="obd-docs-upload-form">
                                        <div className="obd-docs-upload-fields-grid">
                                            {/* Upload File */}
                                            <div className="obd-docs-field">
                                                <label className="obd-docs-field-label obd-docs-field-label--required">
                                                    Upload File
                                                </label>
                                                <div className="obd-docs-file-input-wrapper">
                                                    <Input
                                                        readOnly
                                                        placeholder="Choose File..."
                                                        value={fileName}
                                                        className="obd-docs-file-input-display"
                                                    />
                                                    <input
                                                        type="file"
                                                        ref={fileInputRef}
                                                        onChange={handleFileChange}
                                                    />
                                                    {fileName && (
                                                        <button
                                                            type="button"
                                                            className="obd-docs-file-delete-btn"
                                                            onClick={handleDeleteFile}
                                                            title="Remove File"
                                                        >
                                                            <Trash size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                                {formSubmitted && !selectedFile && (
                                                    <span className="obd-docs-field-error">Please choose file</span>
                                                )}
                                            </div>

                                            {/* Assign Category */}
                                            <div className="obd-docs-field">
                                                <label className="obd-docs-field-label obd-docs-field-label--required">
                                                    Assign Category
                                                </label>
                                                <Select
                                                    placeholder="Select Category"
                                                    value={selectedCategory}
                                                    onChange={(val) => setSelectedCategory(val)}
                                                    style={{ width: '100%' }}
                                                    options={categories.map((c) => ({
                                                        label: c.Text,
                                                        value: c.Value,
                                                    }))}
                                                />
                                                {formSubmitted && !selectedCategory && (
                                                    <span className="obd-docs-field-error">Please choose category</span>
                                                )}
                                            </div>

                                            {/* Upload To Sunnova (Conditional on capability) */}
                                            {showUploadToSunnovaCap && (
                                                <>
                                                    <div className="obd-docs-checkbox-row">
                                                        <Checkbox
                                                            checked={uploadSunnova}
                                                            disabled={!isContractSigned}
                                                            onChange={(e) => setUploadSunnova(e.target.checked)}
                                                        >
                                                            Upload To Sunnova
                                                        </Checkbox>
                                                    </div>

                                                    {/* Project Block Name (Conditional) */}
                                                    {uploadSunnova && projectBlocks.length > 0 && (
                                                        <div className="obd-docs-field">
                                                            <label className="obd-docs-field-label obd-docs-field-label--required">
                                                                Project Block Name
                                                            </label>
                                                            <Select
                                                                placeholder="Select Project Block"
                                                                value={selectedProjectBlock}
                                                                onChange={(val) => setSelectedProjectBlock(val)}
                                                                style={{ width: '100%' }}
                                                                options={projectBlocks.map((b) => ({
                                                                    label: b.BlockName,
                                                                    value: b.BlockId,
                                                                }))}
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Comments */}
                                            <div className="obd-docs-field obd-docs-field--full">
                                                <label className="obd-docs-field-label">Comments</label>
                                                <TextArea
                                                    rows={2}
                                                    value={comments}
                                                    onChange={(e) => setComments(e.target.value)}
                                                    placeholder="Enter comments (optional)"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Middle Section: Box Files Toggle & Embed */}
                    <div className="obd-docs-box-section">
                        <button
                            type="button"
                            className="obd-docs-load-box-btn"
                            onClick={handleLoadBoxFiles}
                            disabled={loadingBox}
                        >
                            <Folder size={16} weight="duotone" />
                            <span>{showBox ? 'Hide Box Files' : 'Load Box Files'}</span>
                        </button>

                        {showBox && (
                            <div className="obd-docs-box-embed-card">
                                <div className="obd-docs-panel__header">
                                    <span>Documents</span>
                                </div>
                                <div className="obd-docs-panel__body" style={{ padding: 0 }}>
                                    {iframeFolderPath ? (
                                        <iframe
                                            title="Box Document Viewer"
                                            src={iframeFolderPath}
                                            className="obd-docs-box-iframe"
                                            allowFullScreen
                                        />
                                    ) : (
                                        <div style={{ padding: '32px', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
                                            No Box folder path is available for this opportunity.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Bottom Section: Miscellaneous Documents and Photos Table */}
                    <div className="obd-docs-table-card">
                        <div className="obd-docs-panel__header">
                            <span>Miscellaneous Documents and Photos</span>
                        </div>
                        <div className="obd-docs-panel__body" style={{ padding: 0 }}>
                            <Table
                                columns={columns}
                                dataSource={documentList}
                                pagination={{
                                    pageSize: 5,
                                    showSizeChanger: false,
                                    showTotal: (total, range) => `${range[0]} to ${range[1]} of ${total}`,
                                }}
                                scroll={{ x: 900 }}
                                locale={{
                                    emptyText: 'No record found',
                                }}
                                size="middle"
                            />
                        </div>
                    </div>
                </div>
            </DashboardModal>
        </>
    );
};

export default DocumentsModal;

